<template>
  <div class="container-fluid d-flex justify-content-center pt-5 p-lg-4">
    <div class="row container mt-3">
      <div class="col-12">
        <h2 class="mb-4">
          <i class="fas fa-box me-2 text-primary"></i>
          {{ isEditMode ? 'Editar Producto' : 'Agregar Producto' }}
        </h2>

        <form @submit.prevent="handleSubmit" class="needs-validation">
          <!-- Información Básica -->
          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 fs-6 py-3">
                <i class="fas fa-info-circle me-2"></i>Información Básica
              </h4>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Nombre del Producto</label>
                  <input v-model="product.name" type="text" class="form-control" required />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Marca</label>
                  <input v-model="product.brand" type="text" class="form-control"  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Categoría <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <select 
                      v-model="product.category" 
                      class="form-select"
                      required
                      v-if="!showCategoryInput"
                    >
                      <option :value="null">Seleccionar categoría</option>
                      <option 
                        v-for="cat in categories" 
                        :key="cat.id" 
                        :value="cat.id"
                      >
                        {{ cat.name }}
                      </option>
                    </select>
                    <input
                      v-if="showCategoryInput"
                      v-model="newCategoryName"
                      type="text"
                      class="form-control"
                      placeholder="Nombre de la nueva categoría"
                    />
                    <button 
                      type="button" 
                      class="btn btn-outline-secondary"
                      @click="toggleCategoryInput"
                      :title="showCategoryInput ? 'Volver a selección' : 'Crear nueva categoría'"
                    >
                      <i :class="showCategoryInput ? 'fas fa-arrow-left' : 'fas fa-plus'"></i>
                    </button>
                    <button 
                      v-if="showCategoryInput" 
                      type="button" 
                      class="btn btn-primary"
                      @click="createNewCategory"
                      :disabled="!newCategoryName.trim() || creatingCategory"
                    >
                      <i class="fas fa-check me-1"></i>
                      {{ creatingCategory ? 'Guardando...' : 'Guardar' }}
                    </button>
                    <button 
                      v-if="!showCategoryInput && product.category" 
                      type="button" 
                      class="btn btn-danger"
                      @click="confirmDeleteCategory"
                      :disabled="deletingCategory"
                      title="Eliminar categoría"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Volumen/Cantidad</label>
                  <input 
                    v-model="product.volume" 
                    type="text" 
                    class="form-control" 
                    placeholder="Ejemplo: 250ml, 1L, 100g"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Dimensiones</label>
                  <input 
                    v-model="product.dimensions" 
                    type="text" 
                    class="form-control" 
                    placeholder="Ejemplo: 10x15x20cm"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Stock</label>
                  <input v-model.number="product.stock" type="number" class="form-control" min="0"  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Precio</label>
                  <!-- Add price comparison info -->
                  <div v-if="product.price > 0" class="price-comparison mb-2 text-muted">
                    <small class="fs-7">
                      <div>Precio en GSG (-30%): ${{ gsgPrice }}</div>
                      <div>Precio sugerido por GSG (+30%): ${{ gsgSuggestedPrice }}</div>
                    </small>
                  </div>
                  <input v-model.number="product.price" type="number" class="form-control" min="0" step="0.01"  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Descuento (%)</label>
                  <input 
                    v-model.number="product.discount" 
                    type="number" 
                    class="form-control" 
                    min="0" 
                    max="100"
                    step="0.01" 
                  />
                </div>
                <div class="col-12">
                  <label class="form-label">Descripción</label>
                  <textarea v-model="product.description" class="form-control" rows="3" ></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Imágenes -->
          <div class="card mb-4">
            <div class="card-header">
              <h4 class="mb-0 py-2 fs-5">
                <i class="fas fa-image me-2"></i>Imágenes
              </h4>
              <small class="text-muted fs-6">
                <i class="fas fa-info-circle me-1"></i>
                Resolución recomendada: 1000x1000px
              </small>
            </div>
            <div class="card-body d-flex row" @paste="handlePaste">
              <div class="d-flex">
                <!-- Vista Previa de Imagen -->
                <div v-if="imagePreview" class="col-md-6 d-flex align-items-end">
                 <img :src="imagePreview" class="img-preview rounded" alt="Vista previa del producto" />
                 <button type="button" class="btn btn-danger ms-4" @click="removeImage('image')">
                   <i class="fas fa-trash"></i>
                 </button>
               </div>
               <div v-if="imagePreview2" class="col-md-6 d-flex align-items-end">
                 <img :src="imagePreview2" class="img-preview rounded" alt="Vista previa del producto" />
                 <button type="button" class="btn btn-danger ms-4" @click="removeImage('image_2')">
                   <i class="fas fa-trash"></i>
                 </button>
               </div>
              </div>
              <div class="col-md-6">
                <label class="form-label pt-3">Imagen principal</label>
                <input type="file" class="form-control" @change="handleUploadImage($event, 'image')" ref="imageUrlInput" />
              </div>
              <div class="col-md-6 ps-0">
                <label class="form-label pt-3">Imagen 2</label>
                <input type="file" class="form-control" @change="handleUploadImage($event, 'image_2')" ref="imageUrlInput2" />
              </div>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              <i class="fas fa-save me-2"></i>
              {{ isSubmitting ? "Guardando..." : (isEditMode ? "Actualizar Producto" : "Guardar Producto") }}
            </button>
            <button type="button" class="btn btn-secondary" @click="resetForm">
              <i class="fas fa-undo me-2"></i>Reiniciar
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal de confirmación para eliminar categoría -->
    <div class="modal fade" id="deleteCategoryModal" tabindex="-1" aria-labelledby="deleteCategoryModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-light">
          <div class="modal-header">
            <h5 class="modal-title" id="deleteCategoryModalLabel">Eliminar Categoría</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="selectedCategoryForDeletion">
            <p>¿Estás seguro que deseas eliminar la categoría "<strong>{{ getCategoryName(selectedCategoryForDeletion) }}</strong>"?</p>
            <p class="text-warning">
              <i class="fas fa-exclamation-triangle me-2"></i>
              Esta acción no se puede deshacer. Solo se pueden eliminar categorías que no estén siendo utilizadas por ningún producto.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="handleDeleteCategory" 
              :disabled="deletingCategory"
            >
              <i class="fas fa-trash me-2"></i>
              {{ deletingCategory ? 'Eliminando...' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getProduct, getCategories, createCategory, deleteCategory } from "@/services/products";
import { createProduct, updateProduct, uploadImage } from "@/services/products";
// toastification
import { useToast } from "vue-toastification";
import "vue-toastification/dist/index.css";
import * as bootstrap from 'bootstrap'; // Correct import for Bootstrap

// Configuración de toastification
const toast = useToast();

// Estructura inicial del producto actualizada
const initialProduct = {
  name: "",
  brand: "",
  category: null,  // Changed to null to force category selection
  category_name: "",
  description: "",
  volume: "",
  dimensions: "",
  price: 0,
  stock: 0,
  discount: 0,  
  image: null,
  image_2: null,
  is_available: true,
  features: []
};

const product = ref({ ...initialProduct });
const imagePreview = ref(null);
const imagePreview2 = ref(null);
const imagePreview3 = ref(null);
const imageUrlInput = ref(null);
const imageUrlInput2 = ref(null);
const imageUrlInput3 = ref(null);
const isSubmitting = ref(false);
const isEditMode = ref(false);
const route = useRoute();
const router = useRouter();
const productId = route.params.productId;
const categories = ref([]);

// New refs for category creation
const showCategoryInput = ref(false);
const newCategoryName = ref('');
const creatingCategory = ref(false);

// New refs for category deletion
let modalInstance = null; // Store the actual Modal instance
const selectedCategoryForDeletion = ref(null);
const deletingCategory = ref(false);

// Function to toggle between category selection and new category input
const toggleCategoryInput = () => {
  showCategoryInput.value = !showCategoryInput.value;
};

// Function to create a new category
const createNewCategory = async () => {
  if (!newCategoryName.value.trim()) {
    toast.error("El nombre de la categoría es requerido");
    return;
  }

  try {
    creatingCategory.value = true;
    const categoryData = { name: newCategoryName.value.trim() };
    const response = await createCategory(categoryData);
    
    // Add the new category to the categories list
    categories.value.push(response);
    
    // Select the newly created category
    product.value.category = response.id;
    
    // Reset the form
    newCategoryName.value = '';
    showCategoryInput.value = false;
    
    toast.success("Categoría creada exitosamente");
  } catch (error) {
    console.error('Error creating category:', error);
    toast.error(error.message || 'Error al crear la categoría');
  } finally {
    creatingCategory.value = false;
  }
};

// Function to get the category name by id
const getCategoryName = (categoryId) => {
  const category = categories.value.find(c => c.id === categoryId);
  return category ? category.name : '';
};

// Open modal to confirm category deletion
const confirmDeleteCategory = () => {
  if (!product.value.category) return;
  
  selectedCategoryForDeletion.value = product.value.category;
  
  // Use Bootstrap modal
  if (!modalInstance) {
    const modalElement = document.getElementById('deleteCategoryModal');
    modalInstance = new bootstrap.Modal(modalElement);
  }
  
  modalInstance.show();
};

// Handle category deletion
const handleDeleteCategory = async () => {
  if (!selectedCategoryForDeletion.value) return;
  
  try {
    deletingCategory.value = true;
    await deleteCategory(selectedCategoryForDeletion.value);
    
    // Remove from categories list
    const index = categories.value.findIndex(c => c.id === selectedCategoryForDeletion.value);
    if (index !== -1) {
      categories.value.splice(index, 1);
    }
    
    // Reset product category if it was the deleted one
    if (product.value.category === selectedCategoryForDeletion.value) {
      product.value.category = null;
    }
    
    // Close modal
    modalInstance.hide();
    selectedCategoryForDeletion.value = null;
    
    toast.success("Categoría eliminada exitosamente");
  } catch (error) {
    console.error('Error deleting category:', error);
    toast.error(error.message || "Error al eliminar la categoría. Es posible que esté siendo utilizada por productos.");
  } finally {
    deletingCategory.value = false;
  }
};

onMounted(async () => {
  try {
    // Load categories first
    const categoriesResponse = await getCategories();
    categories.value = categoriesResponse;
    console.log('Categories loaded:', categories.value);

    if (productId) {
      isEditMode.value = true;
      try {
        isSubmitting.value = true; // Show loading state
        const productData = await getProduct(productId);
        
        if (!productData) {
          throw new Error('No se encontró el producto');
        }
  
        // Set preview images if they exist
        if (productData.image) {
          imagePreview.value = productData.image;
        }
        if (productData.image_2) {
          imagePreview2.value = productData.image_2;
        }
  
        // Update product data - explicitly handle category
        product.value = {
          ...initialProduct,
          ...productData,
          // If category comes as an object with id, use that, otherwise use the id directly
          category: productData.category?.id || productData.category || null,
          brand: productData.brand || '',
          description: productData.description || '',
          features: productData.features || []
        };
  
        console.log('Product loaded successfully:', product.value);
      } catch (error) {
        console.error('Error loading product:', error);
        toast.error(error.message || 'Error al cargar el producto');
        // Redirect back to products list if product not found
        router.push('/admin/productos');
      } finally {
        isSubmitting.value = false;
      }
    }

    // Wait for the DOM to be fully rendered before trying to initialize modals
    await nextTick();
    // Initialize modal after the DOM is updated
    const modalElement = document.getElementById('deleteCategoryModal');
    if (modalElement) {
      modalInstance = new bootstrap.Modal(modalElement);
    }
  } catch (error) {
    console.error('Error in component initialization:', error);
    toast.error('Error al cargar las categorías');
  }
});

const handleUploadImage = async (event, imageField) => {
  const file = event.target.files[0];
  try {
    const imageUrl = await uploadImage(file);
    product.value[imageField] = imageUrl;
    
    if (imageField === 'image') {
      imagePreview.value = imageUrl;
    } else if (imageField === 'image_2') {
      imagePreview2.value = imageUrl;
    } else if (imageField === 'image_3') {
      imagePreview3.value = imageUrl;
    }
  } catch (error) {
    console.error('Error uploading image:', error);
    toast.error('Error al subir la imagen');
  }
};

const handlePaste = async (event) => {
  const items = event.clipboardData.items;
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf("image") !== -1) {
      const file = items[i].getAsFile();
      await handleUploadImage({ target: { files: [file] } }, 'image');
    }
  }
};

const handleSubmit = async () => {
  try {
    if (!product.value.name?.trim()) {
      toast.error('El nombre del producto es requerido');
      return;
    }

    if (!product.value.category) {
      toast.error('Por favor selecciona una categoría');
      return;
    }

    const productToSend = {
      name: product.value.name.trim(),
      brand: (product.value.brand || '').trim(),
      description: (product.value.description || '').trim(),
      category: parseInt(product.value.category), // Ensure category is a number
      price: Number(product.value.price || 0),
      stock: Number(product.value.stock || 0),
      discount: Number(product.value.discount || 0),
      volume: (product.value.volume || '').trim(),
      dimensions: (product.value.dimensions || '').trim(),
      image: product.value.image,
      image_2: product.value.image_2,
      is_available: true
    };

    console.log('Sending product data:', productToSend);
    isSubmitting.value = true;

    if (isEditMode.value) {
      await updateProduct(productId, productToSend);
      toast.success("Producto actualizado exitosamente");
    } else {
      await createProduct(productToSend);
      toast.success("Producto creado exitosamente");
    }

    await router.push('/admin/productos');
  } catch (error) {
    console.error('Error saving product:', error);
    toast.error(error.response?.data?.message || error.message || "Error al guardar el producto");
  } finally {
    isSubmitting.value = false;
  }
};

const removeImage = (imageField) => {
  product.value[imageField] = null;
  if (imageField === 'image') {
    imagePreview.value = null;
    if (imageUrlInput.value) {
      imageUrlInput.value.value = "";
    }
  } else if (imageField === 'image_2') {
    imagePreview2.value = null;
    if (imageUrlInput2.value) {
      imageUrlInput2.value.value = "";
    }
  } else if (imageField === 'image_3') {
    imagePreview3.value = null;
    if (imageUrlInput3.value) {
      imageUrlInput3.value.value = "";
    }
  }
};

const resetForm = () => {
  product.value = { 
    ...initialProduct 
  };
  imagePreview.value = null;
  imagePreview2.value = null;
  imagePreview3.value = null;
  if (imageUrlInput.value) {
    imageUrlInput.value.value = "";
  }
  if (imageUrlInput2.value) {
    imageUrlInput2.value.value = "";
  }
  if (imageUrlInput3.value) {
    imageUrlInput3.value.value = "";
  }
};

// Add these computed properties near the beginning of your script
const gsgPrice = computed(() => {
  if (!product.value.price) return 0;
  return Math.round(product.value.price * 0.7);
});

const gsgSuggestedPrice = computed(() => {
  if (!product.value.price) return 0;
  return Math.round(gsgPrice.value * 1.3);
});
</script>

<script>
  export default{
    name: "ProductForm"
  }
</script>

<style scoped>
.card {
  border: none;
  border-radius: 8px;
  background-color: var(--black-color-3);
  color: var(--white-color-2);
}

.card-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

input, textarea,
.form-select {
  background-color: var(--black-color-2);
  color: var(--white-color-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

a,
select {
  background-color: var(--black-color-3);
  color: var(--white-color-2);
}

option {
  background-color: var(--black-color-3);
  color: var(--white-color-2);
}

.img-preview {
  max-width: 200px;
  max-height: 200px;
  object-fit: contain;
}

.price-preview {
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  background: rgba(40, 167, 69, 0.1);
  border-radius: 4px;
}

.card-header small {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.8rem;
  opacity: 0.8;
}

.features-list {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 0.5rem;
}

.feature-item {
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateX(5px);
}

.feature-item .btn-outline-danger {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

/* Add styles for the category creation button */
.input-group .btn-outline-secondary {
  border-color: rgba(255, 255, 255, 0.1);
  color: var(--white-color-2);
}

.input-group .btn-outline-secondary:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.input-group .btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.modal-content {
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.modal-header, .modal-footer {
  border-color: rgba(255, 255, 255, 0.1);
}

.price-comparison {
  font-size: 0.85rem;
  padding: 0.5rem;
  background-color: var(--black-color-2);
  border-radius: 4px;
  border-left: 3px solid var(--primary-color);
}
</style>